// src/context/PaymentContext.jsx

import React, {createContext, useContext, useState, useCallback, useEffect} from 'react';
import { toast } from 'react-toastify';
import { PAYMENT_STATUS } from '../config/payments.js';
import { paymentSettingsService } from '../services/paymentSettingsService.js';
import { paymentProviderFactory } from '../services/paymentProviderFactory.js';
import appConfig from "../config.jsx";
import { useAuthFetch } from "../hooks/useAuthFetch";


const PaymentContext = createContext();

export function PaymentProvider({ children }) {
    const [paymentState, setPaymentState] = useState({
        status: PAYMENT_STATUS.PENDING,
        currentPayment: null,
        paymentMethod: null,
        provider: null,
        providerInstance: null,
        restaurantSettings: null,
        fullPaymentData: null,
        error: null
    });
    const { authFetch } = useAuthFetch();
    const [loadingSettings, setLoadingSettings] = useState(false);

    const updatePaymentState = useCallback((updates) => {
        setPaymentState(prev =>
            typeof updates === "function"
                ? updates(prev)
                : { ...prev, ...updates }
        );
    }, []);

    const loadRestaurantSettings = useCallback(async (restaurantId, amount = null) => {
        setLoadingSettings(true);

        try {
            paymentSettingsService.setAuthFetch(authFetch);
            const settings = await paymentSettingsService.loadRestaurantPaymentSettings(restaurantId, amount);
            const fullData = paymentSettingsService.getFullPaymentData
                ? paymentSettingsService.getFullPaymentData(restaurantId)
                : null;

            updatePaymentState({
                restaurantSettings: settings,
                fullPaymentData: fullData
            });

            return settings;
        } catch (error) {
            console.error('Failed to load payment settings:', error);
            toast.error('Ошибка загрузки настроек оплаты');
            return null;
        } finally {
            setLoadingSettings(false);
        }
    }, [updatePaymentState]);

    const initPayment = useCallback(async (orderData, userData) => {
        try {
            console.log('[PaymentProcessor] updatePaymentState');
            updatePaymentState({ status: PAYMENT_STATUS.PROCESSING });
            const body = {
                orderId: orderData.id,
                paymentMethod: orderData.paymentMethod,
                restaurantId: orderData.restaurantId ?? orderData.restaurantID ?? paymentState.restaurantSettings?.restaurantId,
                userId: userData?.id,
                userEmail: userData?.email,
                userPhone: userData?.phone,
            };
            // testScenario передаётся только в dev-окружении через UI-переключатель
            if (process.env.NODE_ENV === 'development') {
                body.testScenario = localStorage.getItem('paymentScenario') || 'SUCCESS';
            }

            const response = await authFetch(`${appConfig.endpoint}/payments/init`, {
                method: 'POST',
                body: JSON.stringify(body)
            });

            console.log('[PaymentProcessor] init response', JSON.stringify(response));

            if (!response.success) {
                throw new Error(response.error || 'Payment initialization failed');
            }

            if (response.redirectRequired && response.checkoutUrl) {
                window.location.href = response.checkoutUrl;
                return response;
            }

            updatePaymentState(prev => ({
                ...prev,
                status: PAYMENT_STATUS.PENDING,
                currentPayment: {
                    ...prev.currentPayment,
                    ...response
                }
            }));

            return response;

        } catch (error) {
            updatePaymentState(prev => ({
                ...prev,
                status: PAYMENT_STATUS.FAILED,
                error: error.message
            }));
            throw error;
        }
    }, [authFetch, updatePaymentState]);

    const startPayment = useCallback(async (paymentData, restaurantId, amount = null, options = {}) => {
        try {
            console.log('startPayment called', { paymentData, restaurantId, amount, options });
            let settings = paymentState.restaurantSettings;

            if (!settings || settings.restaurantId !== restaurantId) {
                console.log('Загружаем настройки для ресторана', restaurantId);
                settings = await loadRestaurantSettings(restaurantId, amount);
            }

            if (!settings) {
                throw new Error('Не удалось загрузить настройки платежей');
            }

            if (!paymentSettingsService.isPaymentActive(settings)) {
                console.warn('Платежная система неактивна', settings);
                throw new Error('Платежная система недоступна для этого ресторана');
            }

            if (paymentData.method && !paymentSettingsService.isMethodAvailable(settings, paymentData.method)) {
                console.warn('Метод оплаты недоступен', paymentData.method, settings);
                throw new Error('Выбранный способ оплаты недоступен');
            }

            // Создаем экземпляр провайдера через фабрику
            const providerInstance = paymentProviderFactory.createProvider(settings, {
                forceMock: options.forceMock,
                forceReal: options.forceReal,
                testScenario: options.testScenario
            });
            console.log('Провайдер создан:', providerInstance);

            // Инициализируем провайдера
            await providerInstance.initialize();
            console.log('Провайдер инициализирован');

            updatePaymentState({
                status: PAYMENT_STATUS.PENDING,
                currentPayment: { ...paymentData, restaurantId },
                paymentMethod: paymentData.method,
                provider: settings.provider,
                providerInstance: providerInstance,
                restaurantSettings: settings,
                error: null
            });
            console.log('Состояние платежа обновлено');

            return providerInstance;
        } catch (error) {
            console.error('Ошибка в startPayment:', error);
            updatePaymentState({
                status: PAYMENT_STATUS.FAILED,
                error: error.message
            });
            throw error;
        }
    }, [paymentState.restaurantSettings, loadRestaurantSettings, updatePaymentState]);

    const processPayment = useCallback(async (orderData, userData) => {
        const { providerInstance, currentPayment } = paymentState;


        if (!providerInstance) {
            throw new Error('Платежный провайдер не инициализирован');
        }

        try {
            // Объединяем данные заказа
            const paymentData = {
                ...currentPayment,
                ...orderData,
                amount: orderData.amount || currentPayment?.amount,
                id: orderData.id || currentPayment?.orderId
            };

            const result = await providerInstance.initPayment(paymentData, userData);

            if (result.success) {
                updatePaymentState({
                    status: PAYMENT_STATUS.SUCCESS,
                    currentPayment: { ...currentPayment, ...result }
                });
            } else {
                throw new Error(result.error || 'Payment failed');
            }

            return result;
        } catch (error) {
            const errorResult = providerInstance.handleError(error, 'processPayment');

            updatePaymentState({
                status: PAYMENT_STATUS.FAILED,
                error: errorResult.error
            });

            throw error;
        }
    }, [paymentState.providerInstance, paymentState.currentPayment, updatePaymentState]);

    const confirmPayment = useCallback(async (paymentData) => {
        const { providerInstance } = paymentState;

        if (!providerInstance) {
            throw new Error('Платежный провайдер не инициализирован');
        }

        try {
            const result = await providerInstance.confirmPayment(paymentData);
            return result;
        } catch (error) {
            console.error('Payment confirmation error:', error);
            throw error;
        }
    }, [paymentState]);

    const completePayment = useCallback((paymentResult) => {
        updatePaymentState({
            status: PAYMENT_STATUS.SUCCESS,
            currentPayment: {
                ...paymentState.currentPayment,
                ...paymentResult
            }
        });
    }, [paymentState.currentPayment, updatePaymentState]);

    const failPayment = useCallback((error) => {
        updatePaymentState({
            status: PAYMENT_STATUS.FAILED,
            error: error.message || 'Payment failed'
        });

        toast.error('Ошибка оплаты: ' + (error.message || 'Неизвестная ошибка'));
    }, [updatePaymentState]);

    const cancelPayment = useCallback(() => {
        const { providerInstance, currentPayment } = paymentState;

        if (providerInstance && currentPayment?.transactionId) {
            providerInstance.cancelPayment(currentPayment.transactionId).catch(console.error);
        }

        updatePaymentState({
            status: PAYMENT_STATUS.CANCELLED,
            error: 'Payment cancelled by user'
        });
    }, [paymentState, updatePaymentState]);

    const resetPayment = useCallback(() => {
        setPaymentState(prev => ({
            status: PAYMENT_STATUS.PENDING,
            currentPayment: null,
            paymentMethod: null,
            provider: null,
            providerInstance: null,
            error: null,
            restaurantSettings: prev.restaurantSettings,
            fullPaymentData: prev.fullPaymentData
        }));
    }, []);

    const getAvailableMethods = useCallback(() => {
        return paymentSettingsService.getAvailableMethods(paymentState.restaurantSettings);
    }, [paymentState.restaurantSettings]);

    const isMethodAvailable = useCallback((method) => {
        return paymentSettingsService.isMethodAvailable(paymentState.restaurantSettings, method);
    }, [paymentState.restaurantSettings]);

    const getProviderInfo = useCallback(() => {
        const { providerInstance } = paymentState;
        if (!providerInstance) return null;

        return {
            name: providerInstance.name,
            testMode: providerInstance.config?.testMode,
            isInitialized: providerInstance.isInitialized()
        };
    }, [paymentState]);

    const value = {
        ...paymentState,
        loadingSettings,
        loadRestaurantSettings,
        startPayment,
        initPayment,
        processPayment,
        confirmPayment,
        completePayment,
        failPayment,
        cancelPayment,
        resetPayment,
        getAvailableMethods,
        isMethodAvailable,
        getProviderInfo,
        getAvailableProviders: () => paymentState.fullPaymentData?.providers || [],
        getDefaultProvider: () => paymentState.fullPaymentData?.defaultProvider || null,
        getProviderMethods: (providerId) => {
            const provider = paymentState.fullPaymentData?.providers?.find(p => p.id === providerId);
            return provider?.methods || [];
        },
        isProcessing: paymentState.status === PAYMENT_STATUS.PROCESSING,
        isSuccess: paymentState.status === PAYMENT_STATUS.SUCCESS,
        isFailed: paymentState.status === PAYMENT_STATUS.FAILED,
        isPending: paymentState.status === PAYMENT_STATUS.PENDING,
        hasActiveSettings: !!paymentState.restaurantSettings &&
            paymentSettingsService.isPaymentActive(paymentState.restaurantSettings)
    };

    return (
        <PaymentContext.Provider value={value}>
            {children}
        </PaymentContext.Provider>
    );
}

export function usePayment() {
    const context = useContext(PaymentContext);
    if (!context) {
        throw new Error('usePayment must be used within a PaymentProvider');
    }
    return context;
}